import {
  RequiredAttributes,
  AllowedAttributeValues,
  OptionalAttributes,
} from "shared/models/ssmlValidator";

// Configuration Constants
export const ALLOWED_TAGS = [
  "speak",
  "say-as",
  "voice",
  "phoneme",
  "prosody",
  "emphasis",
  "lang",
  "break",
  "style",
];

export const REQUIRED_ATTRIBUTES: RequiredAttributes = {
  phoneme: ["ph", "alphabet"],
  emphasis: ["level"],
  lang: ["language"],
  style: ["name", "intensity"],
  break: ["time"],
};

export const OPTIONAL_ATTRIBUTES: OptionalAttributes = {
  prosody: ["pitch", "contour", "volume", "rate", "duration"],
  voice: ["name", "language"],
  lang: ["accent"],
  "say-as": ["interpret-as", "language", "format"],
};

export const ALLOWED_ATTRIBUTE_VALUES: AllowedAttributeValues = {
  "say-as": {
    "interpret-as": [
      "characters",
      "ordinal",
      "cardinal",
      "digits",
      "spell-out",
      "decimal",
      "fraction",
      "roman",
      "currency",
      "date",
      "time",
      "unit",
    ],
    format: [
      "dmy",
      "mdy",
      "ymd",
      "dm",
      "md",
      "ym",
      "my",
      "y",
      "m",
      "d",
      "hms12",
      "hms24",
    ],
    language: [
      // "de-DE",
      // "de-AT",
      // "en-GB",
      // "zh-CN",
      // "it-IT",
      // "tr-TR",
      // "pt-BR",
      // "ja-JP",
    ],
  },
  voice: {
    name: [
      // "karlheinz",
      // "ludwig",
      // "annie",
      // "benjamin",
      // "emilie",
      // "oskar",
      // "veronika",
      // "caroline",
      // "ethel",
      // "thomas",
      // "helen",
      // "george",
      // "john",
      // "ned",
      // "nancy",
      // "pauline",
      // "serge",
      // "pierre",
      // "francoise",
      // "soledad",
      // "gustavo",
    ],
    language: [
      // "de-DE",
      // "de-AT",
      // "en-GB",
      // "zh-CN",
      // "it-IT",
      // "tr-TR",
      // "pt-BR",
      // "ja-JP",
    ],
  },
  phoneme: {
    alphabet: ["ipa", "x-sampa"],
  },
  prosody: {
    pitch: ["x-low", "low", "medium", "high", "x-high", "default"],
    volume: ["silent", "x-soft", "soft", "medium", "loud", "x-loud"],
    rate: ["x-slow", "slow", "default", "fast", "x-fast"],
    contour: [],
  },
  emphasis: {
    level: ["none/reduced", "moderate", "strong", "x-strong"],
  },
  lang: {
    language: [
      // "de-DE",
      // "de-AT",
      // "en-GB",
      // "zh-CN",
      // "it-IT",
      // "tr-TR",
      // "pt-BR",
      // "ja-JP",
    ],
    accent: ["none", "x-weak", "weak", "medium", "strong", "x-strong"],
  },
  style: {
    name: [
      "happy",
      "sad",
      "whispering",
      "neutral",
      "question",
      "question",
      "angry",
      "helpful",
      "surprised",
      "positive",
      "advertising",
      "soothing",
      "commercials",
      "news",
      "service",
      "storytelling",
      "voice over",
      "voice assistant",
    ],
    intensity: ["x-weak", "weak", "medium", "strong", "x-strong"],
  },
};

export const SELF_CLOSING_TAGS = ["break"];

export const NESTING_RULES: Record<string, string[] | "none" | "any"> = {
  break: "none",
  "say-as": ["break"],
  speak: "any",
  voice: "any",
  prosody: "any",
  emphasis: "any",
  phoneme: ["break"],
  lang: "any",
};

export const SELF_NESTING_FORBIDDEN = new Set([
  "phoneme",
  "voice",
  "lang",
  "prosody",
  "emphasis",
  "lang",
  "speak",
  "style",
]);

export const SAY_AS_CONTENT_PATTERNS: Record<
  string,
  {
    pattern?: RegExp;
    validator?: (content: string, format?: string) => boolean;
    description: string;
  }
> = {
  cardinal: {
    pattern: /^[+-]?\d+(?:[.,]\d{3})*(?:[.,]\d+)?[%x]?$/i,
    description:
      "Must be a number with optional + or - sign, optional thousand separators, decimals, and optional trailing % or x",
  },
  ordinal: {
    pattern: /^\d+(-?st|-?nd|-?rd|-?th)?$/i,
    description:
      "Must be an integer with optional ordinal suffixes st, nd, rd, or th, with or without leading dash",
  },
  characters: {
    pattern: /^[a-zA-Z\s]+$/,
    description: "Must contain only alphabetic characters",
  },
  "spell-out": {
    // eslint-disable-next-line no-useless-escape
    pattern: /^[a-zA-Z0-9\s\-/§.+\[\]{}]+$/,
    description:
      "Must contain alphanumeric characters, symbols (-, /, §, ., +), and can include [...] for roman numerals or {...} for grouped spelling",
  },
  digits: {
    pattern: /^\d+$/,
    description: "Must contain only digits",
  },
  date: {
    validator: (content: string, format?: string) => {
      if (!format) return true;

      const trimmed = content.trim();
      const separators = ["-", "/", "."];
      const usedSeparator = separators.find((sep) => trimmed.includes(sep));
      const formatChars = format.split("");

      if (formatChars.length > 1 && !usedSeparator) return false;

      const parts = usedSeparator ? trimmed.split(usedSeparator) : [trimmed];
      if (parts.length !== formatChars.length) return false;

      for (let i = 0; i < formatChars.length; i++) {
        const part = parts[i];
        const formatChar = formatChars[i];
        if (!/^\d+$/.test(part)) return false;
        const num = parseInt(part, 10);

        if (formatChar === "d" && (num < 1 || num > 31)) return false;
        if (formatChar === "m" && (num < 1 || num > 12)) return false;
        if (formatChar === "y" && num < 0) return false;
      }

      return true;
    },
    description:
      "Must match the specified date format (e.g., mm/dd/yyyy, dd-mm-yyyy)",
  },
};
